import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPrice, formatTimeWindowDotted } from '@klareospace/pharos-lib-front-utils';
import type { HistoryEntry } from '@klareospace/pharos-lib-types';
import {
  type AddressDetailField,
  Button,
  ErrorBanner,
  HistoryFeed,
  ORDER_STATUS_BADGE_COLORS,
  type HistoryFeedDiffOperationLabels,
} from '@klareospace/pharos-lib-web-utils';

import { mapHistoryEntries } from '../utils/history-mapper';
import { resolveOrderStatusLabel } from '../utils/lifecycle-mapper';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderHistorySectionProps {
  readonly history: HistoryEntry[] | undefined;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly onRetry: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderHistorySection({
  history,
  isError,
  isLoading,
  onRetry,
}: OrderHistorySectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { i18n, t } = useTranslation('orders');

  const entries = useMemo(
    () => (history === undefined ? [] : mapHistoryEntries(history, { t, tCommon })),
    [history, t, tCommon],
  );

  const diffOperationLabels: HistoryFeedDiffOperationLabels = {
    added: tCommon('history.diff.added'),
    removed: tCommon('history.diff.removed'),
    updated: tCommon('history.diff.updated'),
  };

  const addressFieldLabels: Record<AddressDetailField, string> = {
    additionalInformations: tCommon('address.accessInstructions'),
    complement: tCommon('address.addressLine2'),
    digicode: tCommon('address.digicode'),
    floor: tCommon('address.floor'),
    hasElevator: tCommon('address.hasElevator'),
  };

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-2">
        <ErrorBanner message={t('history.error')} />
        <Button onClick={onRetry} size="sm" variant="outline">
          {tCommon('errors.retry')}
        </Button>
      </div>
    );
  }

  return (
    <HistoryFeed
      diffOperationLabels={diffOperationLabels}
      emptyMessage={t('history.empty')}
      entries={entries}
      formatters={{
        addressFieldLabels,
        formatAmount: (value, currency): string => formatPrice({ currency, value }, i18n.language),
        formatBoolean: (value): string => tCommon(value ? 'history.yes' : 'history.no'),
        formatStatus: (status): string => resolveOrderStatusLabel(status, t, tCommon),
        formatTimeslot: (start, end): string => formatTimeWindowDotted({ end: new Date(end), start: new Date(start) }),
        statusBadgeColors: ORDER_STATUS_BADGE_COLORS,
      }}
      isLoading={isLoading || history === undefined}
    />
  );
}
